import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useState, useRef, useMemo } from "react";
import styled from "styled-components";

import { BulkClaimActionsBar } from "../../../components/BulkEditClaimsModal/BulkClaimActionsBar";
import { ClaimDetailsModal } from "../../../components/ClaimDetailsModal/ClaimDetailsModal";
import {
  RENDERING_TAXONOMY_FILTER_OPTIONS,
  renderingTaxonomyLabel,
} from "../../../constants/renderingTaxonomies";
import { UserRole } from "../../../constants/userRole";
import { useUserWithAccount } from "../../../context/UserAccountProvider";
import { parseDate } from "../../../helpers/date";
import useLocalStorageState from "../../../hooks/useLocalStorageState";
import {
  useGetClaimsQuery,
  useLazyExportClaimsQuery,
  useGetDistinctClassificationsQuery,
  useGetDistinctDenialClassificationsQuery,
  useGetDistinctClearPayersQuery,
  useGetDistinctEdiFilesQuery,
  useGetClaimByIdQuery,
  useBackfillMedicareExpectedMutation,
} from "../../../store/queries/claims";
import {
  IClaim,
  CLAIM_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  CHARGE_BELOW_CONTRACT_RATE_CATEGORIES,
  EFFECTIVE_YEAR_MISSING_CATEGORIES,
  TC_COMPONENT_VARIANT_CATEGORIES,
  MEDICARE_RATE_CAP_CATEGORIES,
  PRICING_ERROR_CATEGORIES,
  RESOLUTION_STATUS_LABELS,
  RESOLUTION_STATUS_COLORS,
  RESOLUTION_FILTER_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  DENIAL_STATUS_FILTER_OPTIONS,
} from "../../../types/claim";
import {
  calculateOOPPercent,
  formatDollars,
  getClaimPatientResponsibility,
} from "../../../utils/claimAdjustments";
import {
  CLAIMS_VISIBLE_COLUMNS_STORAGE_KEY,
  runClaimsVisibleColumnsMigrations,
} from "./claimsVisibleColumns.utils";

// CLE-2512: the toggleable data columns, in display order. The leading selection
// checkbox is a control column and is always rendered (not listed here). `key` drives
// both the show/hide MultiSelect and the render switch below; `header` is its label.
const CLAIM_COLUMN_DEFS: { key: string; header: string }[] = [
  { key: "patientName", header: "Patient Name" },
  { key: "serviceDate", header: "Service Date" },
  { key: "eventCount", header: "Events" },
  { key: "claimPayerName", header: "Payer Name" },
  { key: "claimNetworkStatus", header: "Network Status" },
  { key: "clearPayerName", header: "Matched Payer" },
  { key: "chargeAmount", header: "Total Charges" },
  { key: "claimAmount", header: "Claim Amount" },
  { key: "expectedAmount", header: "Expected Amount" },
  // CLE-2914: what Medicare would allow, shown beside the payer's expected amount. A user's
  // stored column layout gains it through claimsVisibleColumns.utils.
  { key: "medicareExpectedAmount", header: "Medicare Expected" },
  { key: "amountDifference", header: "Difference" },
  { key: "variancePercent", header: "Variance" },
  { key: "remitImplantAllowedInCents", header: "Remit Implant Amount" },
  { key: "caseSignature", header: "Case Signature" },
  { key: "renderingTaxonomy", header: "Taxonomy" },
  { key: "denialClassification", header: "Denial Classification" },
  { key: "edi837CaseSignature", header: "837 Case Signature" },
  { key: "allowableAccuracyStatus", header: "Accuracy" },
  { key: "matched837", header: "Matched 837" },
  { key: "claimCopay", header: "Claim Copay" },
  { key: "claimDeductible", header: "Claim Deductible" },
  { key: "claimCoinsurance", header: "Claim Coinsurance" },
  { key: "coinsurancePercent", header: "Coinsurance %" },
  { key: "claimPatientOOP", header: "Claim Pt OOP" },
  { key: "matchConfidence", header: "Match" },
  { key: "resolutionStatus", header: "Status" },
];

const DEFAULT_VISIBLE_COLUMN_KEYS = CLAIM_COLUMN_DEFS.map((c) => c.key);

// CLE-2628: a combined row (separate claim numbers priced as one encounter) must be recognisable
// in the list itself, with the claim numbers folded into it (Ani). Hover the tag for the full list.
// Module-level so the column body is a stable function, not a component re-created every render.
const eventCountTemplate = (rowData: IClaim) => {
  const combined = rowData.combinedClaimNumbers ?? [];
  return (
    <span>
      {rowData.eventCount != null ? rowData.eventCount : "—"}
      {combined.length > 0 && (
        <Tag
          value={`Combined +${combined.length}`}
          severity="info"
          title={`Combined with: ${combined.join(", ")}`}
          style={{ marginLeft: "0.4rem", fontSize: "0.7rem" }}
        />
      )}
    </span>
  );
};

const CaseSignatureLink = styled.span`
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0056b3;
  }
`;

const CompactDataTable = styled(DataTable)`
  /* Reduce cell padding */
  .p-datatable-tbody > tr > td {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
  }

  /* Reduce header padding and enable wrapping */
  .p-datatable-thead > tr > th {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    line-height: 1.3 !important;
  }

  /* Ensure column content wraps */
  .p-column-title {
    white-space: normal !important;
    word-wrap: break-word !important;
  }
`;

// CLE-2565 (Ani): in the chip filters, a long selected value (e.g. a taxonomy) overran the
// input and sat on top of the clear "x", making it invisible/unclickable — users clicked the
// panel's search "x" instead, which doesn't clear the selection. Reserve room on the right for
// the clear icon + dropdown chevron and let a long chip truncate, so the clear "x" is always
// visible and clickable. Applied to every filter MultiSelect for consistent behavior.
const FilterMultiSelect = styled(MultiSelect)`
  /* && doubles the styled class to out-specify PrimeReact's theme rules without !important. */
  && .p-multiselect-label {
    padding-right: 3.75rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  && .p-multiselect-token {
    min-width: 0;
    max-width: 100%;
  }
  && .p-multiselect-token .p-multiselect-token-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  && .p-multiselect-clear-icon {
    z-index: 2;
  }
`;

interface IClaimsListProps {
  accountId: string;
}

function ClaimsList({ accountId }: IClaimsListProps) {
  const toast = useRef<Toast>(null);
  const { userInfo } = useUserWithAccount();
  const isSuperAdmin = userInfo?.scope === UserRole.SUPER_ADMIN;

  // Filter state - non-persisted
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<Date[]>([]);

  // Unified filter - includes both payment types and categories (like CBCR)
  const [persistedFilters, setSelectedFilters] = useLocalStorageState<string[]>(
    `clearhealth_claims_filters_selected_${accountId}`,
    ["all"]
  );

  // A selection persisted before an option was removed (CLE-2836 dropped the
  // prior-global-surgery bucket) is a value PAYMENT_TYPE_OPTIONS no longer knows: the
  // MultiSelect renders it as a blank chip, and if it was the only selection the list
  // silently falls back to every payment type under that blank chip. Drop anything the
  // options no longer offer — generically, so the next option removal is covered too —
  // and fall back to the same "all" the hook defaults to when nothing is left.
  const selectedFilters = useMemo(() => {
    const knownValues = new Set(PAYMENT_TYPE_OPTIONS.map((o) => o.value));
    const kept = persistedFilters.filter((value) => knownValues.has(value));
    return kept.length > 0 ? kept : ["all"];
  }, [persistedFilters]);

  // CLE-2225: denial status filter — orthogonal to payment type, combinable.
  const [selectedDenialStatuses, setSelectedDenialStatuses] =
    useLocalStorageState<string[]>(
      `clearhealth_claims_filters_denialStatuses_${accountId}`,
      []
    );

  // CLE-2320: denial classification filter — orthogonal to denial status. Narrows to
  // claims whose Claim.denialClassificationCode matches one of the selected codes.
  const [selectedDenialClassifications, setSelectedDenialClassifications] =
    useLocalStorageState<string[]>(
      `clearhealth_claims_filters_denialClassifications_${accountId}`,
      []
    );

  // CLE-2489: rendering-taxonomy filter — narrows to claims whose rolled-up
  // Claim.renderingTaxonomy (rendering-provider specialty) matches a selected code.
  const [selectedTaxonomies, setSelectedTaxonomies] = useLocalStorageState<
    string[]
  >(`clearhealth_claims_filters_renderingTaxonomies_${accountId}`, []);

  // Filter state - persisted to localStorage
  const [minDifferencePercent, setMinDifferencePercent] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_minPercent_${accountId}`, undefined);

  // CLE-2565: upper bound on the difference percentage. Paired with minDifferencePercent.
  const [maxDifferencePercent, setMaxDifferencePercent] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_maxPercent_${accountId}`, undefined);

  // CLE-2512: minimum absolute dollar-difference filter (|amountDifference| >= $X).
  const [minDifferenceAmount, setMinDifferenceAmount] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_minAmount_${accountId}`, undefined);

  // CLE-2914: a stored column layout never gains a column added later, so the one-time
  // migrations add new columns to it. The lazy initializer runs once per mount, before the
  // hook below reads the stored array.
  useState(() => runClaimsVisibleColumnsMigrations());

  // CLE-2512: column show/hide. Persisted GLOBALLY (not per-account) so a user's
  // column layout — e.g. hiding Patient Name for a demo — carries across hospitals.
  const [visibleColumns, setVisibleColumns] = useLocalStorageState<string[]>(
    CLAIMS_VISIBLE_COLUMNS_STORAGE_KEY,
    DEFAULT_VISIBLE_COLUMN_KEYS
  );

  // CLE-2166: numeric range filter on episode event count.
  const [minEventCount, setMinEventCount] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_minEvents_${accountId}`, undefined);
  const [maxEventCount, setMaxEventCount] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_maxEvents_${accountId}`, undefined);

  // Resolution status filter - default to "not_reviewed" to show unprocessed items
  const [resolutionFilter, setResolutionFilter] = useLocalStorageState<
    "not_reviewed" | "in_review" | "all"
  >(`clearhealth_claims_filters_resolution_${accountId}`, "not_reviewed");

  // AI Classification filter - default to "all" to show everything
  const [classificationFilter, setClassificationFilter] =
    useLocalStorageState<string>(
      `clearhealth_claims_filters_classification_${accountId}`,
      "all"
    );

  // CLE-2565: Clear Payer filter — multi-select, persisted. Each entry is a payer id (as a
  // string) or the 'no-match' sentinel; empty array = show all payers.
  const [payerIdFilters, setPayerIdFilters] = useLocalStorageState<string[]>(
    `clearhealth_claims_filters_payerIds_${accountId}`,
    []
  );

  // CLE-2491: EDI file filter - persisted to localStorage; empty array = show all files
  const [ediFileFilter, setEdiFileFilter] = useLocalStorageState<string[]>(
    `clearhealth_claims_filters_ediFiles_${accountId}`,
    []
  );

  // Patient class filter - persisted to localStorage
  const [patientClassFilter, setPatientClassFilter] = useLocalStorageState<
    "inpatient" | "outpatient" | "all"
  >(`clearhealth_claims_filters_patientClass_${accountId}`, "all");

  // Claim category filter (form type + place of service) - persisted to localStorage
  const [claimCategoryFilter, setClaimCategoryFilter] = useLocalStorageState<
    | "institutional"
    | "professional-fac"
    | "professional-nonfac"
    | "anesthesia"
    | "all"
  >(`clearhealth_claims_filters_claimCategory_${accountId}`, "all");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sorting state - default to difference ascending (most negative first = largest underpayments)
  const [sortField, setSortField] = useState("serviceDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IClaim | null>(null);

  // CLE-2172: bulk-edit selection (single-page only). Clears on every page,
  // pageSize, sort, or filter change. Backend caps at 25 per call; default
  // pageSize is 25 so "select all on page" naturally hits the cap. The
  // `isDataSelectable` guard prevents going over on the 50-per-page view.
  const BULK_CLAIM_UPDATE_MAX = 25;
  const [selectedClaims, setSelectedClaims] = useState<IClaim[]>([]);

  // Navigation state for similar claims - maintains history stack for back navigation
  const [navigatedClaimId, setNavigatedClaimId] = useState<string | null>(null);
  const [claimHistory, setClaimHistory] = useState<IClaim[]>([]);

  // Compute API params from unified dropdown selection
  const hasCBCR = selectedFilters.includes(
    "CATEGORY_CHARGE_BELOW_CONTRACT_RATE"
  );
  // CLE-2326: the new data-quality bucket — claims priced off a contract that doesn't cover
  // the claim's date of service. Behaves like CBCR: opt in to view; excluded from default
  // payment-type views.
  const hasEYM = selectedFilters.includes("CATEGORY_EFFECTIVE_YEAR_MISSING");
  // CLE-2957: claims the pricing engine failed on — no expected amount at all. Hidden from every
  // default view. Choosing "Pricing error (needs review)" opens a DEDICATED review view: unless
  // "all" is also selected, it wins over every payment type and category bucket (see
  // `pricingErrorView` below), because these claims have no money for those filters to act on —
  // their paymentType is always UNKNOWN, so combining would either list nothing or everything.
  const hasPricingError = selectedFilters.includes("CATEGORY_PRICING_ERROR");
  // CLE-2462: opt-in bucket for TC-component-variant claims (hidden from the default
  // underpayment worklist). Selecting it forwards the category so the backend opts them back in.
  const hasTC = selectedFilters.includes("CATEGORY_TC_COMPONENT_VARIANT");
  // CLE-2489: opt-in bucket for FEHB Medicare rate-cap (RARC N6) claims, hidden from the
  // default underpayment worklist. Selecting it forwards the category so the backend opts in.
  const hasMedicareRateCap = selectedFilters.includes(
    "CATEGORY_MEDICARE_RATE_CAP"
  );
  // Categories forwarded to the backend as an explicit category filter, opting the
  // worklist-suppressed claims back in. Undefined when none selected, matching the API's
  // "no filter" shape. CLE-2836: GLOBAL_PERIOD_BUNDLED is deliberately not here — Ani asked for
  // the global-period bucket to be removed from the view, so the SPA never forwards it.
  const optInCategories = [
    ...(hasTC ? ["TC_COMPONENT_VARIANT"] : []),
    ...(hasMedicareRateCap ? ["MEDICARE_RATE_CAP"] : []),
  ];
  const hasAll = selectedFilters.includes("all");
  // CLE-2957: the dedicated pricing-error review view (see hasPricingError above).
  const pricingErrorView = hasPricingError && !hasAll;
  const optInCategoriesParam =
    optInCategories.length > 0 && !pricingErrorView
      ? optInCategories
      : undefined;
  const paymentTypeValues = selectedFilters.filter(
    (v) => !v.startsWith("CATEGORY_")
  );

  const apiPaymentTypes =
    hasAll || pricingErrorView || paymentTypeValues.length === 0
      ? ["all"]
      : paymentTypeValues;

  const getApiCBCRFilter = (): "include" | "exclude" | "only" => {
    if (hasAll || pricingErrorView) return "include";
    if (hasCBCR && paymentTypeValues.length === 0) return "only";
    if (hasCBCR) return "include";
    return "exclude";
  };
  const apiCBCRFilter = getApiCBCRFilter();

  const getApiEYMFilter = (): "include" | "exclude" | "only" => {
    if (hasAll || pricingErrorView) return "include";
    if (hasEYM && paymentTypeValues.length === 0 && !hasCBCR) return "only";
    if (hasEYM) return "include";
    return "exclude";
  };
  const apiEYMFilter = getApiEYMFilter();

  const getApiPricingErrorFilter = (): "include" | "exclude" | "only" => {
    if (hasAll) return "include";
    if (pricingErrorView) return "only";
    return "exclude";
  };
  const apiPricingErrorFilter = getApiPricingErrorFilter();

  // Export state
  const [exportTrigger, { isLoading: isExporting }] =
    useLazyExportClaimsQuery();

  // CLE-2914: SuperAdmin-only backfill of the Medicare expected amount.
  const [backfillMedicareExpected, { isLoading: isBackfillingMedicare }] =
    useBackfillMedicareExpectedMutation();

  // Fetch distinct classifications for the filter dropdown
  const { data: classificationsData } = useGetDistinctClassificationsQuery({
    accountId,
  });

  // CLE-2565: fetch the deterministic denial classification codes that actually occur on this
  // account, so the Denial Classification filter lists only those (not the full taxonomy).
  const { data: denialClassificationsData } =
    useGetDistinctDenialClassificationsQuery({ accountId });

  // Fetch distinct Clear payers for the filter dropdown
  const { data: clearPayersData } = useGetDistinctClearPayersQuery({
    accountId,
  });

  // CLE-2491: fetch the EDI files that produced claims for this account (filter dropdown)
  const { data: ediFilesData } = useGetDistinctEdiFilesQuery({ accountId });

  // Fetch navigated claim by ID (for similar claims navigation)
  const { data: navigatedClaimData, isFetching: isNavigatedClaimFetching } =
    useGetClaimByIdQuery(
      { claimId: navigatedClaimId! },
      { skip: !navigatedClaimId }
    );

  // Handle navigation to a similar claim - push current claim to history
  const handleNavigateToClaim = (claimId: string) => {
    if (selectedRow) {
      setClaimHistory((prev) => [...prev, selectedRow]);
    }
    setNavigatedClaimId(claimId);
  };

  // Handle back navigation - pop from history
  const handleBackToClaim = () => {
    if (claimHistory.length > 0) {
      const previousClaim = claimHistory[claimHistory.length - 1];
      setClaimHistory((prev) => prev.slice(0, -1));
      setSelectedRow(previousClaim);
    }
  };

  // Update selectedRow when navigated claim data is fetched
  React.useEffect(() => {
    if (navigatedClaimData && !isNavigatedClaimFetching) {
      setSelectedRow(navigatedClaimData);
      setNavigatedClaimId(null); // Clear navigation state
    }
  }, [navigatedClaimData, isNavigatedClaimFetching]);

  // Clear history when modal is closed
  const handleCloseModal = () => {
    setSelectedRow(null);
    setClaimHistory([]);
  };

  // Fetch data - filter by payment types
  const { data, isLoading, isError, error } = useGetClaimsQuery({
    accountId,
    search: search || undefined,
    startDate: dateRange[0] || undefined,
    endDate: dateRange[1] || undefined,
    sortField,
    sortOrder,
    page,
    pageSize,
    minDifferencePercent,
    maxDifferencePercent,
    minDifferenceAmount,
    minEventCount,
    maxEventCount,
    chargeBelowContractRateFilter: apiCBCRFilter,
    effectiveYearMissingFilter: apiEYMFilter,
    pricingErrorFilter: apiPricingErrorFilter,
    // CLE-2462 / CLE-2489: forwarding an opt-in category un-hides the worklist-suppressed claims.
    categories: optInCategoriesParam,
    paymentTypes: apiPaymentTypes,
    denialStatuses:
      selectedDenialStatuses.length > 0 ? selectedDenialStatuses : undefined,
    denialClassificationCodes:
      selectedDenialClassifications.length > 0
        ? selectedDenialClassifications
        : undefined,
    renderingTaxonomies:
      selectedTaxonomies.length > 0 ? selectedTaxonomies : undefined,
    resolutionFilter,
    classificationFilter:
      classificationFilter === "all" ? undefined : classificationFilter,
    payerIdFilters: payerIdFilters.length > 0 ? payerIdFilters : undefined,
    patientClassFilter:
      patientClassFilter === "all" ? undefined : patientClassFilter,
    claimCategoryFilter:
      claimCategoryFilter === "all" ? undefined : claimCategoryFilter,
    ediConversionFileIds: ediFileFilter.length > 0 ? ediFileFilter : undefined,
  });

  // Show error toast
  React.useEffect(() => {
    if (isError) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.data?.message || "Failed to load claims",
      });
    }
  }, [isError, error]);

  // Currency formatter
  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return "-";
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Template functions
  const dateTemplate = (
    rowData: IClaim,
    field: "patientDOB" | "serviceDate"
  ) => {
    const date = rowData[field];
    return date ? parseDate(date) : "-";
  };

  const currencyTemplate = (
    rowData: IClaim,
    field:
      | "chargeAmount"
      | "expectedAmount"
      | "medicareExpectedAmount"
      | "claimAmount"
      | "amountDifference"
  ) => {
    return formatCurrency(rowData[field] ?? null);
  };

  // Total Charges template with badge for charge below contract rate
  // (variance-flavored, "info" severity) or CLE-2326 EFFECTIVE_YEAR_MISSING
  // (data-quality warning — under/overpayment label is unreliable for these rows).
  // Charge-Based Pricing and GLOBAL_PERIOD_BUNDLED (CLE-2836) claims intentionally do not
  // show a badge — the global period is a normal pricing adjustment, not a flag.
  const chargeAmountTemplate = (rowData: IClaim) => {
    const formattedAmount = formatCurrency(rowData.chargeAmount);
    const category = rowData.category ?? "";
    const isChargeBelowContract =
      CHARGE_BELOW_CONTRACT_RATE_CATEGORIES.includes(category);
    const isEffectiveYearMissing =
      EFFECTIVE_YEAR_MISSING_CATEGORIES.includes(category);
    const isTcComponentVariant =
      TC_COMPONENT_VARIANT_CATEGORIES.includes(category);
    const isMedicareRateCap = MEDICARE_RATE_CAP_CATEGORIES.includes(category);
    const isPricingError = PRICING_ERROR_CATEGORIES.includes(category);

    if (
      !isChargeBelowContract &&
      !isEffectiveYearMissing &&
      !isTcComponentVariant &&
      !isMedicareRateCap &&
      !isPricingError
    ) {
      return formattedAmount;
    }

    const categoryKey =
      rowData.category ?? CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE;

    // CLE-2957: a pricing-engine failure is the one bucket with no expected amount at all.
    let icon = "pi pi-info-circle";
    if (isPricingError) icon = "pi pi-times-circle";
    else if (isEffectiveYearMissing) icon = "pi pi-exclamation-triangle";

    return (
      <div className="flex flex-column align-items-start gap-1">
        <span>{formattedAmount}</span>
        <Tag
          value={CATEGORY_LABELS[categoryKey]}
          severity={CATEGORY_COLORS[categoryKey]}
          icon={icon}
        />
      </div>
    );
  };

  const nullableTemplate = (value: string | null) => {
    return value || "-";
  };

  // CLE-2563 — Difference template. For a NOT_ADDITIONAL claim the payer bundled the
  // implant into the case rate and paid in full, so paymentType was flipped to CORRECT
  // and the claim left the underpayment worklist. Showing the raw (negative) gap here
  // still reads as an underpayment (per Ani), so we surface a "Bundled" tag instead.
  // The raw amountDifference is unchanged in the DB / claim detail modal / Excel export.
  const differenceTemplate = (rowData: IClaim) => {
    if (rowData.implantPaymentClassification === "NOT_ADDITIONAL") {
      return <Tag value="Bundled" severity="info" />;
    }
    return currencyTemplate(rowData, "amountDifference");
  };

  // Variance % template - calculate percentage difference
  const variancePercentTemplate = (rowData: IClaim) => {
    // CLE-2563 — a bundled implant (NOT_ADDITIONAL) closed the gap; the raw variance
    // reads as an underpayment, so blank it (see differenceTemplate).
    if (rowData.implantPaymentClassification === "NOT_ADDITIONAL") {
      return "-";
    }

    if (
      rowData.expectedAmount === null ||
      rowData.amountDifference === null ||
      rowData.expectedAmount === 0
    ) {
      return "-";
    }

    // Formula: (Math.abs(amountDifference) / expectedAmount) * 100
    const percentage =
      (Math.abs(rowData.amountDifference) / rowData.expectedAmount) * 100;

    return `${percentage.toFixed(1)}%`;
  };

  // CLE-2563 — Remit Implant Amount. Ani: the column only shows a value when the implant is an
  // ACTUAL ADDITIONAL payment (a carve-out paid on top of a full procedure payment). Bundled
  // implants (NOT_ADDITIONAL) and everything else render "-".
  const remitImplantAmountTemplate = (rowData: IClaim) => {
    if (rowData.implantPaymentClassification !== "ADDITIONAL") return "-";
    return formatCurrency(rowData.remitImplantAllowedInCents ?? null);
  };

  // Helper function to format case signature with line breaks (4 codes per line)
  const formatCaseSignatureWithLineBreaks = (caseSignature: string) => {
    // Split by comma to get array of "CODE:qty" items
    const items = caseSignature.split(",");

    // Group into chunks of 4
    const chunks: string[][] = [];
    for (let i = 0; i < items.length; i += 4) {
      chunks.push(items.slice(i, i + 4));
    }

    // Return JSX with each chunk on its own line
    return (
      <>
        {chunks.map((chunk, index) => (
          <div key={index}>{chunk.join(",")}</div>
        ))}
      </>
    );
  };

  // Patient name link template - opens modal
  const patientNameTemplate = (rowData: IClaim) => {
    const handleClick = () => {
      setSelectedRow(rowData);
      setModalVisible(true);
    };

    return (
      <CaseSignatureLink onClick={handleClick}>
        <span data-pii="patient-name">{rowData.patientName}</span>
      </CaseSignatureLink>
    );
  };

  // Case signature template - plain text with line breaks
  const caseSignatureTemplate = (rowData: IClaim) => {
    return formatCaseSignatureWithLineBreaks(rowData.caseSignature);
  };

  // Accuracy status template - shows icon based on allowable accuracy
  const accuracyStatusTemplate = (rowData: IClaim) => {
    if (!rowData.precalculatedAllowable) {
      return <span>-</span>;
    }

    const { allowableAccuracyStatus, missingCodes } =
      rowData.precalculatedAllowable;

    if (allowableAccuracyStatus) {
      return (
        <span
          title="Complete - All procedure codes found"
          style={{ color: "#28a745" }}
        >
          <i className="pi pi-check-circle" style={{ fontSize: "1.2rem" }} />
        </span>
      );
    }

    const missingCount = missingCodes?.length || 0;
    return (
      <span
        title={`Incomplete - ${missingCount} code(s) missing`}
        style={{ color: "#ffc107" }}
      >
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "1.2rem" }}
        />
      </span>
    );
  };

  // Matched 837 indicator - green check if an 837 claim was matched to this 835
  const matched837Template = (rowData: IClaim) => {
    const count = rowData.matched837Claims?.length ?? 0;
    if (count === 0) {
      return <span>-</span>;
    }
    return (
      <span
        title={`${count} matched 837 claim${count === 1 ? "" : "s"}`}
        style={{ color: "#28a745" }}
      >
        <i className="pi pi-check-circle" style={{ fontSize: "1.2rem" }} />
      </span>
    );
  };

  // ===== CLAIM-SIDE TEMPLATES (from 835 PR adjustments) =====
  // Note: 835 adjustment amounts are in dollars (not cents) - use formatDollars
  // CLE-2512: read line items episode-aware — episode roots keep their rolled-up lines
  // on `episodeNetLineItems`, leaving `lineItems` null. Reading `lineItems` directly
  // blanked these columns on the list even though export/remit populated them.
  // CLE-2804: a header-only remit has no lines at all — its PR-1/2/3 live in
  // `claimSummaryData.adjustments`. `getClaimPatientResponsibility` reads lines, else that.

  // Claim patient responsibility (deductible + copay + coinsurance)
  const claimPatientOOPTemplate = (rowData: IClaim) => {
    const resp = getClaimPatientResponsibility(rowData);
    return resp.totalOOP > 0 ? formatDollars(resp.totalOOP) : "-";
  };

  // Claim deductible (PR adjustment code 1)
  const claimDeductibleTemplate = (rowData: IClaim) => {
    const resp = getClaimPatientResponsibility(rowData);
    return resp.deductible > 0 ? formatDollars(resp.deductible) : "-";
  };

  // Claim coinsurance (PR adjustment code 2)
  const claimCoinsuranceTemplate = (rowData: IClaim) => {
    const resp = getClaimPatientResponsibility(rowData);
    return resp.coinsurance > 0 ? formatDollars(resp.coinsurance) : "-";
  };

  // Claim copay (PR adjustment code 3)
  const claimCopayTemplate = (rowData: IClaim) => {
    const resp = getClaimPatientResponsibility(rowData);
    return resp.copay > 0 ? formatDollars(resp.copay) : "-";
  };

  // CLE-2512: Taxonomy — rendering-provider specialty rolled up to the episode root
  // (CLE-2437). Show the human label when the code is in the curated list, else the raw code.
  const taxonomyTemplate = (rowData: IClaim) => {
    const code = rowData.renderingTaxonomy;
    if (!code) return "-";
    return renderingTaxonomyLabel(code);
  };

  // CLE-2512: Denial Classification — the classifier title (falling back to its code).
  const denialClassificationTemplate = (rowData: IClaim) => {
    return (
      rowData.denialClassificationTitle ||
      rowData.denialClassificationCode ||
      "-"
    );
  };

  // CLE-2512: 837 Case Signature — the matched-837 signature (DRG for inpatient,
  // service lines for outpatient); distinct from the 835-derived Case Signature.
  const edi837CaseSignatureTemplate = (rowData: IClaim) => {
    return rowData.edi837CaseSignature
      ? formatCaseSignatureWithLineBreaks(rowData.edi837CaseSignature)
      : "-";
  };

  // Coinsurance percent = coinsurance / (claim amount – deductible)
  // Uses claim-side values (from 835 PR adjustments) in dollars
  const coinsurancePercentTemplate = (rowData: IClaim) => {
    // Get claim-side values (in dollars from 835)
    const claimResp = getClaimPatientResponsibility(rowData);

    // Get claim amount (in cents) - this is the allowable/paid amount from 835
    if (!rowData.claimAmount) return "-";

    const claimAmountInDollars = rowData.claimAmount / 100;

    // Calculate: Coinsurance% = coinsurance / (claimAmount - deductible)
    const coinsurancePercent = calculateOOPPercent(
      claimResp.coinsurance, // Just coinsurance portion
      claimAmountInDollars,
      claimResp.deductible
    );

    if (coinsurancePercent === null) return "-";
    return `${coinsurancePercent.toFixed(1)}%`;
  };

  // Match confidence indicator
  const matchConfidenceTemplate = (rowData: IClaim) => {
    if (!rowData.order || rowData.orderMatchConfidence === null) {
      return <span>-</span>;
    }

    const confidence = rowData.orderMatchConfidence ?? 0;
    let color: string;
    let title: string;

    if (confidence >= 0.9) {
      color = "#28a745"; // green
      title = `High confidence (${(confidence * 100).toFixed(0)}%)`;
    } else if (confidence >= 0.7) {
      color = "#ffc107"; // yellow
      title = `Medium confidence (${(confidence * 100).toFixed(0)}%)`;
    } else {
      color = "#dc3545"; // red
      title = `Low confidence (${(confidence * 100).toFixed(0)}%)`;
    }

    return (
      <span title={title} style={{ color }}>
        <i className="pi pi-link" style={{ fontSize: "1.2rem" }} />
      </span>
    );
  };

  // Resolution status template - shows color-coded status badge
  const resolutionStatusTemplate = (rowData: IClaim) => {
    const status = rowData.resolutionStatus;
    const label = RESOLUTION_STATUS_LABELS[status || "null"];
    const severity = RESOLUTION_STATUS_COLORS[status || "null"];

    return <Tag value={label} severity={severity} />;
  };

  // Handle DataTable events
  const onSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder === 1 ? "asc" : "desc");
    setPage(1); // Reset to first page
    setSelectedClaims([]);
  };

  const onPage = (event: any) => {
    setPage(event.page + 1); // PrimeReact uses 0-indexed pages
    setPageSize(event.rows);
    setSelectedClaims([]);
  };

  // Handle filter changes - reset to page 1
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleDateRangeChange = (e: { value: CalendarValueType }) => {
    const dates = e.value as Date[];
    setDateRange(dates || []);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleMinDifferencePercentChange = (value: string) => {
    const numValue = value === "" ? undefined : parseFloat(value);
    setMinDifferencePercent(numValue);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleMaxDifferencePercentChange = (value: string) => {
    const parsed = value === "" ? undefined : parseFloat(value);
    setMaxDifferencePercent(
      parsed === undefined || Number.isNaN(parsed) ? undefined : parsed
    );
    setPage(1);
    setSelectedClaims([]);
  };

  const handleMinDifferenceAmountChange = (value: string) => {
    const parsed = value === "" ? undefined : parseFloat(value);
    setMinDifferenceAmount(
      parsed === undefined || Number.isNaN(parsed) ? undefined : parsed
    );
    setPage(1);
    setSelectedClaims([]);
  };

  const handleEventCountChange =
    (setter: (value: number | undefined) => void) => (value: string) => {
      const numValue = value === "" ? undefined : parseInt(value, 10);
      setter(Number.isNaN(numValue as number) ? undefined : numValue);
      setPage(1);
      setSelectedClaims([]);
    };

  const handleResolutionFilterChange = (
    value: "not_reviewed" | "in_review" | "all"
  ) => {
    setResolutionFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleClassificationFilterChange = (value: string) => {
    setClassificationFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleFiltersChange = (value: string[]) => {
    const prevHadAll = selectedFilters.includes("all");
    const nowHasAll = value.includes("all");
    const hasCBCRValue = value.includes("CATEGORY_CHARGE_BELOW_CONTRACT_RATE");

    if (nowHasAll && !prevHadAll) {
      setSelectedFilters(["all"]);
    } else if (nowHasAll && value.length > 1) {
      setSelectedFilters(value.filter((v) => v !== "all"));
    } else {
      setSelectedFilters(value);
    }
    setPage(1);
    setSelectedClaims([]);
  };

  // CLE-2565: multi-select Clear Payer. `showClear` "x" fires onChange with null; coerce to []
  // so the query param (payerIdFilters.length) doesn't throw.
  const handlePayerIdFilterChange = (value: string[] | null) => {
    setPayerIdFilters(value ?? []);
    setPage(1);
    setSelectedClaims([]);
  };

  // CLE-2491: EDI file filter change. PrimeReact's `showClear` "x" fires onChange
  // with `null` (not []), so coerce to [] — otherwise `ediFileFilter.length` in the
  // query params throws and the clear appears to do nothing (CLE-2489, Ani item 8).
  const handleEdiFileFilterChange = (value: string[] | null) => {
    setEdiFileFilter(value ?? []);
    setPage(1);
    setSelectedClaims([]);
  };

  const handlePatientClassFilterChange = (
    value: "inpatient" | "outpatient" | "all"
  ) => {
    setPatientClassFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  const handleClaimCategoryFilterChange = (
    value:
      | "institutional"
      | "professional-fac"
      | "professional-nonfac"
      | "anesthesia"
      | "all"
  ) => {
    setClaimCategoryFilter(value);
    setPage(1);
    setSelectedClaims([]);
  };

  // Handle export button click
  const handleExport = async () => {
    try {
      const result = await exportTrigger({
        accountId,
        search: search || undefined,
        startDate: dateRange[0] || undefined,
        endDate: dateRange[1] || undefined,
        sortField,
        sortOrder,
        minDifferencePercent,
        maxDifferencePercent,
        minDifferenceAmount,
        chargeBelowContractRateFilter: apiCBCRFilter,
        effectiveYearMissingFilter: apiEYMFilter,
        pricingErrorFilter: apiPricingErrorFilter,
        // CLE-2462 / CLE-2489: keep export parity with the visible list — opt suppressed claims in.
        categories: optInCategoriesParam,
        paymentTypes: apiPaymentTypes,
        denialStatuses:
          selectedDenialStatuses.length > 0
            ? selectedDenialStatuses
            : undefined,
        denialClassificationCodes:
          selectedDenialClassifications.length > 0
            ? selectedDenialClassifications
            : undefined,
        renderingTaxonomies:
          selectedTaxonomies.length > 0 ? selectedTaxonomies : undefined,
        resolutionFilter,
        classificationFilter:
          classificationFilter === "all" ? undefined : classificationFilter,
        payerIdFilters: payerIdFilters.length > 0 ? payerIdFilters : undefined,
        patientClassFilter:
          patientClassFilter === "all" ? undefined : patientClassFilter,
        claimCategoryFilter:
          claimCategoryFilter === "all" ? undefined : claimCategoryFilter,
        ediConversionFileIds:
          ediFileFilter.length > 0 ? ediFileFilter : undefined,
      }).unwrap();

      // Download the file using the signed URL
      window.open(result.downloadUrl, "_blank");

      toast.current?.show({
        severity: "success",
        summary: "Export Started",
        detail: "Your Excel file is being downloaded",
      });
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Export Failed",
        detail: (err as any)?.data?.message || "Failed to export claims",
      });
    }
  };

  // CLE-2914: queue the Medicare expected amount for every claim on this account that has none
  // yet. The server answers 202 and prices in the background, so the values only show on a
  // later refetch.
  const handleBackfillMedicareExpected = () => {
    confirmDialog({
      header: "Compute Medicare expected",
      icon: "pi pi-calculator",
      message:
        "Price every claim on this account that has no Medicare expected yet? It runs in the background; refresh the list in a few minutes.",
      acceptLabel: "Compute",
      rejectLabel: "Cancel",
      accept: async () => {
        try {
          const result = await backfillMedicareExpected({ accountId }).unwrap();
          // Nothing queued: every claim already has one, or none of the account's
          // providers has a Medicare contract to price against.
          if (result.candidateCount === 0) {
            toast.current?.show({
              severity: "info",
              summary: "Medicare expected",
              detail:
                "Nothing to compute: no claims on this account need a Medicare expected",
            });
            return;
          }
          toast.current?.show({
            severity: "success",
            summary: "Medicare expected",
            detail: `Started: ${result.candidateCount} claim${
              result.candidateCount === 1 ? "" : "s"
            } queued`,
          });
        } catch (err) {
          const status = (err as any)?.status;
          toast.current?.show({
            severity: "error",
            summary: "Medicare expected",
            detail:
              status === 409
                ? (err as any)?.data?.message ||
                  "The Medicare payer is not set up."
                : "Could not start computing the Medicare expected amounts.",
          });
        }
      },
    });
  };

  // CLE-2512: render one data column by key. Keeps each column's exact props (sortable,
  // sortField, body, style) so the show/hide MultiSelect only gates visibility. Only
  // columns whose field is in the backend sortField whitelist are marked `sortable`.
  const renderColumn = (key: string) => {
    switch (key) {
      case "patientName":
        return (
          <Column
            key={key}
            field="patientName"
            header="Patient Name"
            sortable
            body={patientNameTemplate}
            style={{ minWidth: "12rem" }}
          />
        );
      case "serviceDate":
        return (
          <Column
            key={key}
            field="serviceDate"
            header="Service Date"
            sortable
            body={(rowData) => dateTemplate(rowData, "serviceDate")}
            style={{ minWidth: "8rem" }}
          />
        );
      case "eventCount":
        return (
          <Column
            key={key}
            field="eventCount"
            header="Events"
            sortable
            body={eventCountTemplate}
            style={{ minWidth: "7rem" }}
          />
        );
      case "claimPayerName":
        return (
          <Column
            key={key}
            field="claimPayerName"
            header="Payer Name"
            sortable
            style={{ minWidth: "12rem" }}
          />
        );
      case "claimNetworkStatus":
        return (
          <Column
            key={key}
            field="claimNetworkStatus"
            header="Network Status"
            body={(rowData) => nullableTemplate(rowData.claimNetworkStatus)}
            style={{ minWidth: "10rem" }}
          />
        );
      case "clearPayerName":
        return (
          <Column
            key={key}
            field="clearPayerName"
            header="Matched Payer"
            body={(rowData) => nullableTemplate(rowData.clearPayerName)}
            style={{ minWidth: "12rem" }}
          />
        );
      case "chargeAmount":
        return (
          <Column
            key={key}
            field="chargeAmount"
            header="Total Charges"
            sortable
            body={chargeAmountTemplate}
            style={{ minWidth: "10rem" }}
          />
        );
      case "claimAmount":
        return (
          <Column
            key={key}
            field="claimAmount"
            header="Claim Amount"
            sortable
            body={(rowData) => currencyTemplate(rowData, "claimAmount")}
            style={{ minWidth: "10rem" }}
          />
        );
      case "expectedAmount":
        return (
          <Column
            key={key}
            field="expectedAmount"
            header="Expected Amount"
            sortable
            body={(rowData) => currencyTemplate(rowData, "expectedAmount")}
            style={{ minWidth: "10rem" }}
          />
        );
      case "medicareExpectedAmount":
        return (
          <Column
            key={key}
            field="medicareExpectedAmount"
            header="Medicare Expected"
            sortable
            // CLE-2914: "-" (never $0.00) when there is none — the payer is Medicare, the claim
            // has no payer, or the backfill has not reached it yet.
            body={(rowData) =>
              currencyTemplate(rowData, "medicareExpectedAmount")
            }
            style={{ minWidth: "10rem" }}
          />
        );
      case "amountDifference":
        return (
          <Column
            key={key}
            field="amountDifference"
            header="Difference"
            sortable
            body={differenceTemplate}
            style={{ minWidth: "10rem" }}
          />
        );
      case "variancePercent":
        return (
          <Column
            key={key}
            header="Variance"
            body={variancePercentTemplate}
            sortable
            sortField="variancePercent"
            style={{ minWidth: "7rem" }}
          />
        );
      case "remitImplantAllowedInCents":
        return (
          <Column
            key={key}
            field="remitImplantAllowedInCents"
            header="Remit Implant Amount"
            body={remitImplantAmountTemplate}
            sortable
            sortField="remitImplantAllowedInCents"
            style={{ minWidth: "12rem" }}
          />
        );
      case "caseSignature":
        return (
          <Column
            key={key}
            field="caseSignature"
            header="Case Signature"
            body={caseSignatureTemplate}
            style={{ minWidth: "12rem" }}
          />
        );
      case "renderingTaxonomy":
        return (
          <Column
            key={key}
            field="renderingTaxonomy"
            header="Taxonomy"
            body={taxonomyTemplate}
            style={{ minWidth: "12rem" }}
          />
        );
      case "denialClassification":
        return (
          <Column
            key={key}
            header="Denial Classification"
            body={denialClassificationTemplate}
            style={{ minWidth: "12rem" }}
          />
        );
      case "edi837CaseSignature":
        return (
          <Column
            key={key}
            header="837 Case Signature"
            body={edi837CaseSignatureTemplate}
            style={{ minWidth: "12rem" }}
          />
        );
      case "allowableAccuracyStatus":
        return (
          <Column
            key={key}
            field="allowableAccuracyStatus"
            header="Accuracy"
            sortable
            body={accuracyStatusTemplate}
            style={{ minWidth: "6rem", textAlign: "center" }}
          />
        );
      case "matched837":
        return (
          <Column
            key={key}
            header="Matched 837"
            body={matched837Template}
            style={{ minWidth: "7rem", textAlign: "center" }}
          />
        );
      case "claimCopay":
        return (
          <Column
            key={key}
            header="Claim Copay"
            body={claimCopayTemplate}
            style={{ minWidth: "7rem" }}
          />
        );
      case "claimDeductible":
        return (
          <Column
            key={key}
            header="Claim Deductible"
            body={claimDeductibleTemplate}
            style={{ minWidth: "8rem" }}
          />
        );
      case "claimCoinsurance":
        return (
          <Column
            key={key}
            header="Claim Coinsurance"
            body={claimCoinsuranceTemplate}
            style={{ minWidth: "8rem" }}
          />
        );
      case "coinsurancePercent":
        return (
          <Column
            key={key}
            header="Coinsurance %"
            body={coinsurancePercentTemplate}
            style={{ minWidth: "7rem" }}
          />
        );
      case "claimPatientOOP":
        return (
          <Column
            key={key}
            header="Claim Pt OOP"
            body={claimPatientOOPTemplate}
            style={{ minWidth: "8rem" }}
          />
        );
      case "matchConfidence":
        return (
          <Column
            key={key}
            header="Match"
            body={matchConfidenceTemplate}
            style={{ minWidth: "5rem", textAlign: "center" }}
          />
        );
      case "resolutionStatus":
        return (
          <Column
            key={key}
            field="resolutionStatus"
            header="Status"
            body={resolutionStatusTemplate}
            style={{ minWidth: "8rem" }}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
      </div>
    );
  }

  const claims = data?.data || [];
  const totalRecords = data?.totalCount || 0;

  return (
    <>
      <Toast ref={toast} />

      {/* Filters Row 1: Search, Date Range, Category */}
      <div className="flex gap-3 mb-2 flex-wrap align-items-center">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search patient, payer, account #, claim #, or CPT codes..."
            className="w-20rem"
          />
        </span>

        <div className="flex flex-column gap-2" style={{ minWidth: "250px" }}>
          <Calendar
            selectionMode="range"
            placeholder="Service Date Range"
            // CLE-2561: pass null (not []) when empty. An empty array passes PrimeReact's
            // isComparable() check, so clicking the month title dereferences value[0] →
            // undefined.getMonth() and crashes the page (PrimeReact 8.7.3 range-calendar bug).
            value={dateRange.length ? dateRange : null}
            onChange={handleDateRangeChange}
            showIcon
            dateFormat="mm/dd/yy"
          />
        </div>

        {/* Category Filter (Payment Types + Categories like CBCR) */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="paymentTypesFilter">Category:</label>
          <FilterMultiSelect
            inputId="paymentTypesFilter"
            value={selectedFilters}
            options={PAYMENT_TYPE_OPTIONS}
            onChange={(e) => handleFiltersChange(e.value)}
            placeholder="Select payment types"
            display="chip"
            style={{ minWidth: "220px" }}
            showSelectAll={false}
          />
        </div>

        {/* CLE-2225: Denial Status filter — orthogonal to payment type, combinable. */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="denialStatusFilter">Denial Status:</label>
          <FilterMultiSelect
            inputId="denialStatusFilter"
            value={selectedDenialStatuses}
            options={DENIAL_STATUS_FILTER_OPTIONS}
            onChange={(e) => {
              setSelectedDenialStatuses(e.value);
              setPage(1);
              setSelectedClaims([]);
            }}
            placeholder="Any denial status"
            display="chip"
            style={{ minWidth: "200px" }}
            showSelectAll={false}
          />
        </div>

        {/* CLE-2320 / CLE-2565: Denial Classification filter — narrows by deterministic
            classification code (e.g., D3 = Non-Covered, E1 = Prior Auth Failure). Options are
            the codes that ACTUALLY occur on this account (from getDistinctDenialClassifications),
            not the full static taxonomy. */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="denialClassificationFilter">
            Denial Classification:
          </label>
          <FilterMultiSelect
            inputId="denialClassificationFilter"
            value={selectedDenialClassifications}
            options={(denialClassificationsData || []).map((c) => ({
              label: `${c.code} — ${c.title}`,
              value: c.code,
            }))}
            onChange={(e) => {
              setSelectedDenialClassifications(e.value ?? []);
              setPage(1);
              setSelectedClaims([]);
            }}
            placeholder="Any classification"
            display="chip"
            filter
            showClear
            maxSelectedLabels={2}
            style={{ minWidth: "240px", maxWidth: "340px" }}
          />
        </div>

        {/* CLE-2489: Rendering Taxonomy filter — narrows by rendering-provider
            specialty (Claim.renderingTaxonomy, rolled up to the episode root). */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="renderingTaxonomyFilter">Taxonomy:</label>
          <FilterMultiSelect
            inputId="renderingTaxonomyFilter"
            value={selectedTaxonomies}
            options={RENDERING_TAXONOMY_FILTER_OPTIONS}
            onChange={(e) => {
              // `showClear` "x" fires onChange with null; coerce to [] so the
              // query param (selectedTaxonomies.length) doesn't throw (Ani item 8).
              setSelectedTaxonomies(e.value ?? []);
              setPage(1);
              setSelectedClaims([]);
            }}
            placeholder="Any taxonomy"
            display="chip"
            filter
            showClear
            maxSelectedLabels={2}
            style={{ minWidth: "240px", maxWidth: "340px" }}
          />
        </div>
      </div>

      {/* Filters Row 2: Status, Classification, Other Filters, Export */}
      <div className="flex gap-3 mb-3 flex-wrap align-items-center justify-content-between">
        <div className="flex gap-3 flex-wrap align-items-center">
          {/* Resolution Status Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="resolutionFilter">Status:</label>
            <Dropdown
              inputId="resolutionFilter"
              value={resolutionFilter}
              options={[...RESOLUTION_FILTER_OPTIONS]}
              onChange={(e) => handleResolutionFilterChange(e.value)}
              placeholder="Select status"
            />
          </div>

          {/* AI Classification Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="classificationFilter">Classification:</label>
            <Dropdown
              inputId="classificationFilter"
              value={classificationFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Not Analyzed", value: "not_analyzed" },
                ...(classificationsData || []).map((c) => ({
                  label: `${c.code} - ${c.title}`,
                  value: c.code,
                })),
              ]}
              onChange={(e) => handleClassificationFilterChange(e.value)}
              placeholder="Select classification"
              style={{ minWidth: "180px" }}
            />
          </div>

          {/* Clear Payer Filter (CLE-2565: multi-select) */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="payerIdFilter">Clear Payer:</label>
            <FilterMultiSelect
              inputId="payerIdFilter"
              value={payerIdFilters}
              options={(clearPayersData || []).map((payer) => ({
                label: payer.name,
                value: payer.id === null ? "no-match" : String(payer.id),
              }))}
              onChange={(e) => handlePayerIdFilterChange(e.value)}
              placeholder="All payers"
              display="chip"
              filter
              showClear
              maxSelectedLabels={2}
              style={{ minWidth: "200px", maxWidth: "340px" }}
            />
          </div>

          {/* EDI File Filter (CLE-2491) */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="ediFileFilter">EDI File:</label>
            <FilterMultiSelect
              inputId="ediFileFilter"
              value={ediFileFilter}
              options={(ediFilesData || []).map((file) => ({
                label: `${file.displayName || file.fileName} (${
                  file.claimCount
                })`,
                value: file.id,
              }))}
              onChange={(e) => handleEdiFileFilterChange(e.value)}
              placeholder="All files"
              display="chip"
              filter
              showClear
              maxSelectedLabels={2}
              style={{ minWidth: "220px", maxWidth: "360px" }}
            />
          </div>

          {/* Patient Class Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="patientClassFilter">Patient Class:</label>
            <Dropdown
              inputId="patientClassFilter"
              value={patientClassFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Inpatient", value: "inpatient" },
                { label: "Outpatient", value: "outpatient" },
              ]}
              onChange={(e) => handlePatientClassFilterChange(e.value)}
              placeholder="Select class"
            />
          </div>

          {/* Claim Category Filter (form type + place of service) */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="claimCategoryFilter">Claim Type:</label>
            <Dropdown
              inputId="claimCategoryFilter"
              value={claimCategoryFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Institutional", value: "institutional" },
                {
                  label: "Professional — Facility",
                  value: "professional-fac",
                },
                {
                  label: "Professional — Non-Facility",
                  value: "professional-nonfac",
                },
                { label: "Anesthesia", value: "anesthesia" },
              ]}
              onChange={(e) => handleClaimCategoryFilterChange(e.value)}
              placeholder="Select claim type"
              style={{ minWidth: "200px" }}
            />
          </div>

          {/* CLE-2565: difference-percentage range. Min and/or Max — a row is kept when its
              |Difference| / Expected * 100 falls within whichever bounds are set. */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="minDifferencePercent">Difference %:</label>
            <InputText
              id="minDifferencePercent"
              type="number"
              placeholder="Min"
              value={minDifferencePercent?.toString() ?? ""}
              onChange={(e) => handleMinDifferencePercentChange(e.target.value)}
              className="w-5rem"
              step="1"
            />
            <span>-</span>
            <InputText
              id="maxDifferencePercent"
              type="number"
              placeholder="Max"
              value={maxDifferencePercent?.toString() ?? ""}
              onChange={(e) => handleMaxDifferencePercentChange(e.target.value)}
              className="w-5rem"
              step="1"
            />
          </div>

          {/* CLE-2512: minimum absolute dollar-difference filter. Matches claims
              where |Difference| >= this many dollars, in either direction. */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="minDifferenceAmount">Min Difference $:</label>
            <InputText
              id="minDifferenceAmount"
              type="number"
              value={minDifferenceAmount?.toString() ?? ""}
              onChange={(e) => handleMinDifferenceAmountChange(e.target.value)}
              className="w-8rem"
              min="0"
              step="1"
            />
          </div>

          {/* CLE-2166: Events numeric range filter. >=2 surfaces complex episodes
              (reprocess chains, bilateral splits, duplicate denials) for review. */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="minEventCount">Events:</label>
            <InputText
              id="minEventCount"
              type="number"
              placeholder="Min"
              value={minEventCount?.toString() ?? ""}
              onChange={(e) =>
                handleEventCountChange(setMinEventCount)(e.target.value)
              }
              className="w-5rem"
              min="0"
              step="1"
            />
            <span>-</span>
            <InputText
              id="maxEventCount"
              type="number"
              placeholder="Max"
              value={maxEventCount?.toString() ?? ""}
              onChange={(e) =>
                handleEventCountChange(setMaxEventCount)(e.target.value)
              }
              className="w-5rem"
              min="0"
              step="1"
            />
          </div>
        </div>

        <div className="flex gap-2 align-items-center">
          {/* CLE-2914: SuperAdmin-only — Ani fills the Medicare expected column herself. */}
          {isSuperAdmin && (
            <Button
              label="Compute Medicare expected"
              icon="pi pi-calculator"
              className="p-button-outlined p-button-secondary"
              onClick={handleBackfillMedicareExpected}
              loading={isBackfillingMedicare}
            />
          )}

          {/* Export Button */}
          <Button
            label="Export to Excel"
            icon="pi pi-file-excel"
            className="p-button-success"
            onClick={handleExport}
            loading={isExporting}
            disabled={isLoading || totalRecords === 0}
          />
        </div>
      </div>

      {/* CLE-2172: bulk actions bar — only renders when the user has selected
          rows. Workflow is single-page: pick rows on this page, apply, then
          paginate and repeat. */}
      {selectedClaims.length > 0 && (
        <BulkClaimActionsBar
          selectedClaims={selectedClaims}
          maxBulkSize={BULK_CLAIM_UPDATE_MAX}
          onClearSelection={() => setSelectedClaims([])}
          accountId={accountId}
          onActionSuccess={() => setSelectedClaims([])}
        />
      )}

      {/* CLE-2512: column show/hide. Persisted globally so the layout carries across
          hospitals (e.g. hide Patient Name for a demo). */}
      <div className="flex justify-content-end mb-2">
        <FilterMultiSelect
          value={visibleColumns}
          options={CLAIM_COLUMN_DEFS}
          optionLabel="header"
          optionValue="key"
          onChange={(e) => setVisibleColumns(e.value)}
          placeholder="Columns"
          maxSelectedLabels={0}
          selectedItemsLabel={`{0} of ${CLAIM_COLUMN_DEFS.length} columns shown`}
          className="w-16rem"
          display="comma"
          filter
          filterPlaceholder="Find column"
          scrollHeight="400px"
        />
      </div>

      {/* Data Table */}
      <CompactDataTable
        value={claims}
        lazy
        paginator
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
        rowsPerPageOptions={[10, 25, 50]}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage={data?.message || "No claims found."}
        selectionMode="checkbox"
        selection={selectedClaims}
        onSelectionChange={(e) => setSelectedClaims(e.value as IClaim[])}
        dataKey="id"
        // Past the cap, disable further row checks. Users on the 50/page view
        // can still de-select; already-selected rows remain selectable.
        isDataSelectable={(e: { data: IClaim }) =>
          selectedClaims.length < BULK_CLAIM_UPDATE_MAX ||
          selectedClaims.some((c) => c.id === e.data.id)
        }
      >
        {/* CLE-2512: selection checkbox is always rendered; data columns are gated
            by the show/hide MultiSelect. `variancePercent` sorts in-memory; the
            eventCount / caseSignature notes above still apply per-column. */}
        <Column selectionMode="multiple" style={{ width: "3rem" }} />
        {CLAIM_COLUMN_DEFS.filter((def) =>
          visibleColumns.includes(def.key)
        ).map((def) => renderColumn(def.key))}
      </CompactDataTable>

      {/* Claim Details Modal */}
      {selectedRow && (
        <ClaimDetailsModal
          visible={modalVisible}
          onHide={() => {
            setModalVisible(false);
            handleCloseModal();
          }}
          accountId={accountId}
          claim={selectedRow}
          onNavigateToClaim={handleNavigateToClaim}
          onBackToClaim={
            claimHistory.length > 0 ? handleBackToClaim : undefined
          }
          isNavigating={isNavigatedClaimFetching}
          onClaimUpdated={(updatedClaim) => {
            // Update the selected claim in the modal
            setSelectedRow(updatedClaim);
            // Note: The claims list is automatically refreshed via RTK Query's
            // cache invalidation (invalidatesTags: ["Claims"] in the toggle mutation)
          }}
        />
      )}
    </>
  );
}

export default ClaimsList;
